// TableFilters.tsx
import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Row,
  Col,
  InputNumber,
  Space,
  Drawer,
  Switch,
} from "antd";
import {
  SearchOutlined,
  ClearOutlined,
  FilterOutlined,
  CloseOutlined,
  ReloadOutlined
} from "@ant-design/icons";
import styled from "styled-components";
import { Moment } from "moment"; // Explicit Moment import
import { RequestStatus } from "./types";
import { useTranslation } from "react-i18next";

const { RangePicker } = DatePicker;
const { Option } = Select;
export type RequestType = "LOAD" | "WITHDRAW";

export interface FilterValues {
  username?: string;
  dateRange?: [Moment, Moment];
  type?: RequestType | "";
  status?: RequestStatus | "";
  amountMin?: number;
  amountMax?: number;
  requestNumber?: string;
  reprocessed?: boolean; // Added field for reprocessed filter
}

export interface CategoryFiltersState {
  showPending: boolean;
  showApproved: boolean;
  showCompleted: boolean;
}

export interface TableFiltersProps {
  onFilter: (filters: FilterValues) => void;
  allowedStatuses?: RequestStatus[];
  isVisible?: boolean;
  tableType: "pending" | "completed" | "approved";
  onOpenDrawer?: () => void;
  activeTab?: 'pending' | 'approved' | 'completed';
}

// Botón para abrir el drawer de filtros
const FilterDrawerButton = styled(Button)`
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .anticon {
    font-size: 16px;
  }
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 16px;
  }
`;

export const TableFilters: React.FC<TableFiltersProps> = ({
  onFilter,
  allowedStatuses = [],
  isVisible = true,
  
  onOpenDrawer,
  activeTab,
}) => {
  const [form] = Form.useForm();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { t } = useTranslation();
  const [reprocessedFilter, setReprocessedFilter] = useState(false);

  useEffect(() => {
    // Initialize form with default values
    form.setFieldsValue({ reprocessed: false });
  }, [form]);

  // Reset form when activeTab changes
  useEffect(() => {
    if (activeTab) {
      form.resetFields();
      setReprocessedFilter(false);
      form.setFieldsValue({ reprocessed: false });
    }
  }, [activeTab, form]);

  const handleFilter = (values: FilterValues) => {

    // Clean undefined/null/empty values from the filter object
    const cleanedValues: any = {};
    Object.keys(values).forEach((key) => {
      const value = values[key as keyof FilterValues];
      // Only include values that are not undefined, null, or empty string
      if (value !== undefined && value !== null && value !== '') {
        cleanedValues[key] = value;
      }
    });

    // Add reprocessed value
    cleanedValues.reprocessed = reprocessedFilter;

    onFilter(cleanedValues);
    setDrawerOpen(false);
  };

  const handleReset = () => {
    form.resetFields();
    setReprocessedFilter(false);
    form.setFieldsValue({ reprocessed: false });
    onFilter({});
    setDrawerOpen(false); // Close drawer after clearing
  };

  const handleReprocessedChange = (checked: boolean) => {
    // Solo actualizar estado local, NO aplicar filtros automáticamente
    setReprocessedFilter(checked);
    form.setFieldsValue({ reprocessed: checked });
  };

  // Custom parser to handle number conversion safely
  const numberParser = (value: string | undefined): number | undefined => {
    if (!value) return undefined;
    const parsed = parseFloat(value.replace(/\$\s?|(,*)/g, ""));
    return isNaN(parsed) ? undefined : parsed;
  };

  // Renderizado del formulario de filtros
  const renderFilterForm = () => (
    <StyledForm
      form={form}
      layout="vertical"
      onFinish={handleFilter}
      size="middle"
    >
      {/* Hidden field to track reprocessed state */}
      <Form.Item name="reprocessed" hidden>
        <Input type="hidden" />
      </Form.Item>

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Form.Item
            name="requestNumber"
            label={t(
              "requestsPanel.tableFilters.form.fields.requestNumber.label"
            )}
          >
            <Input
              placeholder={t(
                "requestsPanel.tableFilters.form.fields.requestNumber.placeholder"
              )}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            name="username"
            label={t("requestsPanel.tableFilters.form.fields.username.label")}
          >
            <Input
              placeholder={t(
                "requestsPanel.tableFilters.form.fields.username.placeholder"
              )}
            />
          </Form.Item>
        </Col>

        {/* Only show status filter for completed tab (multiple statuses) */}
        {activeTab === 'completed' && allowedStatuses.length > 0 && (
          <Col span={24}>
            <Form.Item
              name="status"
              label={t("requestsPanel.tableFilters.form.fields.status.label")}
            >
              <Select
                placeholder={t(
                  "requestsPanel.tableFilters.form.fields.status.placeholder"
                )}
                allowClear
              >
                <Option value="">
                  {t(
                    "requestsPanel.tableFilters.form.fields.status.options.all"
                  )}
                </Option>
                {allowedStatuses.map((status) => (
                  <Option key={status} value={status}>
                    {status === "COMPLETED" && "Completadas"}
                    {status === "REJECTED" && "Rechazadas"}
                    {status === "CANCELED" && "Canceladas"}
                    {!["COMPLETED", "REJECTED", "CANCELED"].includes(status) && status}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}

        <Col span={24}>
          <Form.Item
            name="dateRange"
            label={t(
              "requestsPanel.tableFilters.form.fields.dateRange.label"
            )}
          >
            <RangePicker style={{ width: "100%" }} />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            label={t("requestsPanel.tableFilters.form.fields.amount.label")}
          >
            <Space.Compact block>
              <Form.Item name="amountMin" noStyle>
                <InputNumber
                  placeholder={t(
                    "requestsPanel.tableFilters.form.fields.amount.min"
                  )}
                  style={{ width: "50%" }}
                  formatter={(value) =>
                    value ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                  }
                  parser={numberParser}
                />
              </Form.Item>
              <Form.Item name="amountMax" noStyle>
                <InputNumber
                  placeholder={t(
                    "requestsPanel.tableFilters.form.fields.amount.max"
                  )}
                  style={{ width: "50%" }}
                  formatter={(value) =>
                    value ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                  }
                  parser={numberParser}
                />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            label={t("requestsPanel.tableFilters.form.fields.reprocessed.label")}
          >
            <Switch
              checked={reprocessedFilter}
              onChange={handleReprocessedChange}
              checkedChildren={<ReloadOutlined />}
              unCheckedChildren=""
            />
          </Form.Item>
        </Col>
      </Row>
    </StyledForm>
  );

  return (
    <>
      {/* Botón para abrir el drawer de filtros */}
      {isVisible && onOpenDrawer && (
        <FilterDrawerButton
          type="default"
          icon={<FilterOutlined />}
          onClick={() => setDrawerOpen(true)}
        >
          {t("requestsPanel.tableFilters.title")}
        </FilterDrawerButton>
      )}

      {/* Drawer de filtros */}
      <Drawer
        title={
          <Space align="center">
            <FilterOutlined />
            {t("requestsPanel.tableFilters.title")}
          </Space>
        }
        placement="right"
        width={400}
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        styles={{
          body: {
            padding: '24px'
          },
          header: {
            padding: '16px 24px'
          },
          footer: {
            borderTop: '1px solid #f0f0f0',
            padding: '16px 24px'
          }
        }}
        closeIcon={<CloseOutlined />}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button
              onClick={handleReset}
              icon={<ClearOutlined />}
            >
              {t("requestsPanel.tableFilters.buttons.clear")}
            </Button>
            <Button
              type="primary"
              onClick={() => form.submit()}
              icon={<SearchOutlined />}
            >
              {t("requestsPanel.tableFilters.buttons.apply")}
            </Button>
          </Space>
        }
      >
        {renderFilterForm()}
      </Drawer>
    </>
  );
};